import React, { useRef } from "react";
import { useSetAtom } from "jotai";
import { useHover } from "frontend/ui-components/floaters/use-hover";
import { Tooltip } from "frontend/ui-components/floaters/tooltip";
import WalkthroughTipPresenter from "frontend/walkthrough-tips/walkthrough-tip-presenter";
import { CursorType } from "../../cursor-type";
import styles from "../side-menu.module.css";
import { WalkthroughTipName } from "frontend/walkthrough-tips/walkthrough-tip-model";
import { sidePaneState, Tab } from "../../side-panel/side-panel";
import { useWindowSize } from "react-use";
import consts from "shared/consts";
import { getMenuItemIdSelector, MenuItem as SideMenuItem } from "./side-menu-items";
import { TipName } from "shared/datamodel/schemas";

export function MenuItem({
  item,
  onItemClick,
  highlight,
  addElementsTip,
  nextTip,
  setShowSidePanel,
  relativeTo,
  isSideMenuOverflow,
}: {
  item: SideMenuItem;
  onItemClick: (item: SideMenuItem, ref: any, isSideMenuOverflow: boolean) => void;
  highlight: boolean;
  addElementsTip: boolean;
  nextTip: TipName | null;
  setShowSidePanel: (enable: boolean) => void;
  relativeTo: any;
  isSideMenuOverflow: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const relativeToOrRef = relativeTo ?? ref;
  const hover = useHover(relativeToOrRef);
  const setSidePaneAtom = useSetAtom(sidePaneState);
  const size = useWindowSize();

  const onClick = item.available ? () => onItemClick(item, relativeToOrRef, isSideMenuOverflow) : undefined;
  highlight ||=
    addElementsTip &&
    (item.type == CursorType.text ||
      item.type == CursorType.stickyNote ||
      item.type == CursorType.draw ||
      item.type == CursorType.connector ||
      item.type == CursorType.shape);
  highlight ||= nextTip == item.tipName && item.tipName != null;

  const className = item.available
    ? highlight
      ? styles.itemContainerSelected
      : styles.itemContainer
    : styles.itemContainerDisabled;

  return (
    <>
      {!item.hidden && [
        <div
          key={item.type}
          ref={relativeToOrRef}
          onClick={onClick}
          className={className}
          data-testid={item.tooltip}
          id={getMenuItemIdSelector(item.type)}
        >
          {React.cloneElement(item.iconRenderer(highlight), { className: styles.iconStretch })}
          {hover && item.tooltip && (
            <Tooltip
              side={"right"}
              label={item.tooltip}
              relativeTo={relativeToOrRef}
              boundary="none"
              design={"default"}
            />
          )}
        </div>,
        item.available && item.tipName && size.height > consts.MIN_SCREEN_HEIGHT_FOR_WALKTHROUGH && (
          <WalkthroughTipPresenter
            key={item.tipName}
            tipName={item.tipName as WalkthroughTipName}
            attachToRef={relativeToOrRef}
            side={"right"}
            onShowAndHide={(enable) => {
              if (item.type == CursorType.frame || item.type == CursorType.comment) {
                setShowSidePanel(enable);
                if (enable) {
                  if (item.type == CursorType.frame) setSidePaneAtom(Tab.frames);
                  if (item.type == CursorType.comment) setSidePaneAtom(Tab.comments);
                }
              }
            }}
          />
        ),
      ]}
    </>
  );
}
